import React, { useMemo } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { BrazilDate } from "../../../utils/BrazilDate";
import { InspecaoSnapshotResponse } from "../../../services/inspecaoService";
import { useInspecaoContext } from "../context/InspecaoContext";

interface InspecaoSnapshotsTableProps {
  snapshots: InspecaoSnapshotResponse[] | undefined;
  isLoading: boolean;
}

const intervalColumns = [
  "Intervalo",
  "Loja",
  "Pizzas produzidas",
  "Tempo médio de produção",
  "Aguardando entrega",
  "Em entrega (rota)",
  "Falta montar",
] as const;

export const InspecaoSnapshotsTable: React.FC<InspecaoSnapshotsTableProps> = ({
  snapshots,
  isLoading,
}) => {
  const { user, selectedLojaId, setSelectedLojaId, lojas } =
    useInspecaoContext();
  const isAdmin = user?.role?.nome?.toLowerCase().includes("admin") ?? false;

  const lojaNomeResumo = useMemo(() => {
    if (selectedLojaId === "all") return "Todas";
    const loja = lojas.find((l: { id: number }) => l.id === selectedLojaId);
    return loja?.nome ?? `Loja #${selectedLojaId}`;
  }, [selectedLojaId, lojas]);

  const showDateColumn = useMemo(() => {
    if (!snapshots?.length) return false;
    return new Set(snapshots.map((s) => s.data)).size > 1;
  }, [snapshots]);

  const columns = useMemo(() => {
    return showDateColumn
      ? (["Data", ...intervalColumns] as const)
      : intervalColumns;
  }, [showDateColumn]);

  const rows = useMemo(() => {
    if (!snapshots?.length) return [];
    let previousQuantity: number | null = null;
    let currentDay: string | null = null;
    return snapshots.map((s) => {
      if (currentDay !== s.data) {
        previousQuantity = null;
        currentDay = s.data;
      }
      const isTotal = !!s.isTotal;
      const previous = previousQuantity;
      const delta =
        !isTotal && previous != null ? s.quantidadeProducoes - previous : null;

      if (!isTotal) {
        previousQuantity = s.quantidadeProducoes;
      }

      return {
        ...s,
        lojaNome: lojaNomeResumo,
        pizzasDelta: delta,
        pizzasPrevious: previous,
      };
    });
  }, [snapshots, lojaNomeResumo]);

  if (isLoading) {
    return (
      <div className="mt-6 rounded-lg bg-white p-4 shadow">
        <h3 className="mb-3 text-sm font-semibold text-neutral-800">
          Tempos por intervalo
        </h3>
        <div className="py-8 text-center text-neutral-500">
          Carregando intervalos...
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 overflow-hidden rounded-lg border border-neutral-200 bg-white shadow">
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">
              Tempos por intervalo
            </h3>
            <p className="mt-0.5 text-xs text-neutral-500">
              Falta montar = pendentes na fila doce/salgada com status pending e
              entrada no dia (igual Painel/Dashboard). Na data de hoje, a faixa
              em que o relógio ainda está aberta mostra o valor ao vivo; faixas
              já encerradas sem registro do cron ficam em branco. Após o cron
              gravar o intervalo, usa o número salvo. A linha &quot;Dia inteiro
              (total)&quot; em hoje também usa o valor na hora da consulta.
            </p>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-sm">
            <span className="text-xs font-medium text-neutral-500">Loja</span>
            <select
              value={selectedLojaId}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedLojaId(
                  value === "all" ? "all" : Number.parseInt(value, 10)
                );
              }}
              disabled={!isAdmin}
              className="cursor-pointer border-none bg-transparent text-sm text-neutral-800 outline-none disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isAdmin && <option value="all">Todas</option>}
              {lojas.map((loja: { id: number; nome: string }) => (
                <option key={loja.id} value={loja.id}>
                  {loja.nome}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead>
            <tr className="bg-neutral-50">
              {columns.map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-neutral-600"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 bg-white">
            {rows.map((row) => (
              <tr
                key={`${row.data}-${row.horario}-${row.id}`}
                className={
                  row.isTotal
                    ? "bg-emerald-50/90 font-semibold hover:bg-emerald-50"
                    : "hover:bg-neutral-50/80"
                }
              >
                {showDateColumn ? (
                  <td className="whitespace-nowrap px-4 py-2 text-sm text-neutral-700">
                    {BrazilDate.formatBR(
                      BrazilDate.fromISOString(row.data),
                      false
                    )}
                  </td>
                ) : null}
                <td className="whitespace-nowrap px-4 py-2 text-sm text-neutral-900">
                  {row.horario}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-neutral-700">
                  {row.lojaNome}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm text-neutral-700">
                  {row.quantidadeProducoes != null ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-block min-w-[4ch] text-right tabular-nums">
                        {row.quantidadeProducoes}
                      </span>
                      {row.isTotal ? null : row.pizzasDelta != null &&
                        row.pizzasDelta !== 0 ? (
                        <span
                          title={
                            row.pizzasPrevious != null
                              ? `Anterior: ${row.pizzasPrevious}`
                              : undefined
                          }
                          className={[
                            "inline-flex min-w-[3.25rem] items-center justify-center gap-0.5 rounded px-2 py-0.5 text-[11px] font-semibold tabular-nums",
                            row.pizzasDelta < 0
                              ? "bg-red-50 text-red-700"
                              : "bg-green-50 text-green-700",
                          ].join(" ")}
                        >
                          {row.pizzasDelta < 0 ? (
                            <ArrowDown className="shrink-0" size={12} />
                          ) : row.pizzasDelta > 0 ? (
                            <ArrowUp className="shrink-0" size={12} />
                          ) : null}
                          <span className="min-w-[2ch] text-center">
                            {Math.abs(row.pizzasDelta)}
                          </span>
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm tabular-nums text-neutral-700">
                  {row.tempoMedioProducao ?? "—"}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm tabular-nums text-neutral-700">
                  {row.tempoMedioAguardandoEntrega ?? "—"}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm tabular-nums text-neutral-700">
                  {row.tempoMedioEmEntrega ?? "—"}
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-sm tabular-nums text-neutral-700">
                  {row.pizzasAProduzir != null ? row.pizzasAProduzir : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
